import { ApiError, api, type RecorderStatusDto } from '../api.js';
import { createPlayer, type Player, type PlayerStatus } from '../player.js';
import { clear, el, formatDuration, toast } from '../ui.js';
import type { StreamQuality } from '../../shared/api-types.js';

/** Live view: video, stream status, quality toggle and the record controls. */

const STATUS_POLL_MS = 2500;

const STATUS_LABELS: Record<PlayerStatus, string> = {
  connecting: 'Connecting…',
  live: 'Live',
  stalled: 'Buffering…',
  unavailable: 'Stream unavailable',
};

type Teardown = () => void;

const buildVideo = (): HTMLVideoElement => {
  const video = el('video', {
    class: 'player__video',
    playsinline: 'playsinline',
    // Autoplay is only permitted while muted; there is no useful audio anyway.
    muted: 'muted',
    autoplay: 'autoplay',
    preload: 'auto',
    controls: 'controls',
  });
  video.muted = true;
  return video;
};

const requestFullscreen = (element: HTMLElement & { webkitEnterFullscreen?: () => void }): void => {
  if (typeof element.requestFullscreen === 'function') {
    void element.requestFullscreen().catch(() => undefined);
    return;
  }
  // iPhone Safari only exposes fullscreen on the video element itself.
  if (typeof element.webkitEnterFullscreen === 'function') element.webkitEnterFullscreen();
};

export const renderLive = (root: HTMLElement): Teardown => {
  clear(root);

  const video = buildVideo();
  const statusPill = el('span', { class: 'pill pill--connecting' }, ['Connecting…']);

  const qualityButton = el('button', { class: 'button button--ghost', type: 'button' }, ['HD']);
  const fullscreenButton = el(
    'button',
    { class: 'button button--ghost', type: 'button', 'aria-label': 'Fullscreen' },
    ['Fullscreen'],
  );

  const recordButton = el('button', { class: 'button button--record', type: 'button' }, [
    'Start recording',
  ]);
  const recordMeta = el('p', { class: 'record__meta' }, ['Not recording']);

  const setPlayerStatus = (status: PlayerStatus): void => {
    statusPill.className = `pill pill--${status}`;
    statusPill.textContent = STATUS_LABELS[status];
  };

  const player: Player = createPlayer({ video, onStatus: setPlayerStatus });

  const setQualityLabel = (quality: StreamQuality): void => {
    qualityButton.textContent = quality === 'hd' ? 'HD' : 'SD';
    qualityButton.setAttribute(
      'aria-label',
      quality === 'hd' ? 'Switch to standard definition' : 'Switch to high definition',
    );
  };
  setQualityLabel(player.quality);

  qualityButton.addEventListener('click', () => {
    const next: StreamQuality = player.quality === 'hd' ? 'sd' : 'hd';
    player.setQuality(next);
    setQualityLabel(next);
  });

  fullscreenButton.addEventListener('click', () => requestFullscreen(video));

  // --- Record controls -----------------------------------------------------

  let recording = false;
  let busy = false;
  let timer: number | null = null;
  let stopped = false;

  const applyStatus = (status: RecorderStatusDto): void => {
    recording = status.state !== 'idle';
    recordButton.disabled = busy || status.state === 'finalizing';

    if (status.state === 'finalizing') {
      recordButton.textContent = 'Saving…';
      recordButton.classList.remove('button--recording');
      recordMeta.textContent = 'Finishing up…';
      return;
    }

    if (status.recording === null) {
      recordButton.textContent = 'Start recording';
      recordButton.classList.remove('button--recording');
      recordMeta.textContent = 'Not recording';
      return;
    }

    recordButton.textContent = 'Stop recording';
    recordButton.classList.add('button--recording');
    const elapsed = formatDuration(status.recording.elapsedSeconds);
    const limit = formatDuration(status.recording.maxSeconds);
    recordMeta.textContent = status.recording.reconnecting
      ? `Recording ${elapsed} — reconnecting to the camera…`
      : `Recording ${elapsed} of ${limit} max`;
  };

  const poll = async (): Promise<void> => {
    try {
      applyStatus(await api.recorderStatus());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw err;
      // A transient poll failure should not spam the user.
      recordMeta.textContent = 'Recording status unavailable';
    }
  };

  const schedulePoll = (): void => {
    if (stopped) return;
    timer = window.setTimeout(() => {
      void poll().finally(schedulePoll);
    }, STATUS_POLL_MS);
  };

  recordButton.addEventListener('click', () => {
    if (busy) return;
    busy = true;
    recordButton.disabled = true;

    void (async () => {
      try {
        if (recording) {
          await api.stopRecording();
          toast('Recording saved.');
        } else {
          await api.startRecording();
          toast('Recording started.');
        }
      } catch (err) {
        toast(err instanceof ApiError ? err.message : 'Could not change the recording.', 'error');
      } finally {
        busy = false;
        await poll();
      }
    })();
  });

  root.append(
    el('section', { class: 'live' }, [
      el('div', { class: 'player' }, [
        video,
        el('div', { class: 'player__overlay' }, [statusPill]),
      ]),
      el('div', { class: 'live__controls' }, [qualityButton, fullscreenButton]),
      el('div', { class: 'record' }, [recordButton, recordMeta]),
    ]),
  );

  void poll().finally(schedulePoll);

  return () => {
    stopped = true;
    if (timer !== null) window.clearTimeout(timer);
    player.destroy();
  };
};
